export type RuntimeMode = "webxr" | "camera-viewer" | "non-ar" | "eighth-wall";

export type RuntimePhase =
  | "idle"
  | "checking"
  | "loading-engine"
  | "requesting-permission"
  | "tracking-initializing"
  | "tracking-limited"
  | "searching-surface"
  | "placed"
  | "paused"
  | "recovering"
  | "error"
  | "stopped";

export type RuntimeStopReason = "user" | "session-ended" | "error" | "unmount";

export interface PlacementPose {
  position: [number, number, number];
  quaternion: [number, number, number, number];
  scale: number;
}

export type PlacementMode = "surface" | "fixed";

export interface RuntimeSupport {
  supported: boolean;
  reason?: string;
}

export interface CapabilityReport {
  secureContext: boolean;
  webxr: boolean;
  camera: boolean;
  coarsePointer: boolean;
  appleMobile: boolean;
  iosVersion: string | null;
  webgl: boolean;
  orientation: boolean;
  wasmSimd: boolean;
  eighthWallEligible: boolean;
  preferredMode: RuntimeMode;
}

export type EngineLoadState = "idle" | "prefetching" | "ready" | "failed";
export type TrackingState = "unavailable" | "initializing" | "limited" | "normal" | "paused";

export interface RuntimeSnapshot {
  mode: RuntimeMode;
  phase: RuntimePhase;
  placement: PlacementPose | null;
  error: string | null;
  placementMode?: PlacementMode;
  engine?: EngineLoadState;
  tracking?: TrackingState;
}

export interface NormalizedScreenPoint {
  x: number;
  y: number;
}

export interface ExternalR3fContext {
  renderer: import("three").WebGLRenderer;
  camera: import("three").PerspectiveCamera;
  parentScene: import("three").Scene;
}

export interface ExternalR3fBridge {
  advance(timestamp: number): void;
  unmount(): void;
}

export interface CameraTrackingStartOptions {
  canvas: HTMLCanvasElement;
  mountScene(context: ExternalR3fContext): Promise<ExternalR3fBridge>;
}

export interface RuntimeAdapter {
  readonly mode: RuntimeMode;
  probe(): Promise<RuntimeSupport>;
  start(): Promise<void>;
  stop(reason: RuntimeStopReason): Promise<void>;
  reposition(): Promise<void>;
  acceptPlacement(pose: PlacementPose): void;
  getSnapshot(): RuntimeSnapshot;
  subscribe(listener: (snapshot: RuntimeSnapshot) => void): () => void;
}

export const DEFAULT_PLACEMENT: PlacementPose = {
  position: [0, -0.55, -3.4],
  quaternion: [0, 0, 0, 1],
  scale: 0.72,
};

// WebXR uses a local-floor reference space. Keep this separate from the
// desktop viewer pose, whose negative Y value is intentionally camera-relative.
export const WEBXR_FIXED_PLACEMENT: PlacementPose = {
  position: [0, 0, -1.5],
  quaternion: [0, 0, 0, 1],
  scale: 0.55,
};
